import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GRADE_LEVELS, COURSES } from "@/lib/constants";
import { toast } from "sonner";
import { Loader2, CheckCircle2, MailWarning } from "lucide-react";
import { PasswordField } from "@/components/PasswordField";
import { ProfileVoicePrivacy } from "./ProfileVoicePrivacy";

export default function Profile() {
  const { user, refresh, logout } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [grade, setGrade] = useState(user?.grade_level || "");
  const [course, setCourse] = useState(user?.selected_course || "");
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [nwStrength, setNwStrength] = useState({ score: 0, valid: false, length: 0 });
  const [pwSaving, setPwSaving] = useState(false);
  const [autosaved, setAutosaved] = useState(false);
  // Snapshot of the last persisted values so we skip redundant saves.
  const lastSaved = useRef({
    name: user?.name || "",
    grade: user?.grade_level || "",
    course: user?.selected_course || "",
  });
  // Skip the very first auto-save trigger caused by state initialisation.
  const firstRun = useRef(true);

  const emailVerified = Boolean(user?.email_verified);

  // Auto-save profile info (name / grade / course) with a short debounce.
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    const trimmedName = name.trim();
    if (trimmedName.length < 2) return; // avoid saving mid-typing empties
    if (
      trimmedName === lastSaved.current.name &&
      grade === lastSaved.current.grade &&
      course === lastSaved.current.course
    ) {
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        await api.patch("/auth/profile", {
          name: trimmedName || null,
          grade_level: grade || null,
          selected_course: course || null,
        });
        if (cancelled) return;
        lastSaved.current = { name: trimmedName, grade, course };
        await refresh();
        setAutosaved(true);
        toast.success("Değişiklikler kaydedildi");
        setTimeout(() => !cancelled && setAutosaved(false), 1800);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      }
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [name, grade, course, refresh]);

  const changePw = async () => {
    if (!cur || !nw) return;
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      setCur("");
      setNw("");
      toast.success("Şifren güncellendi");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <AppShell variant="student">
      <div className="max-w-3xl mx-auto p-5 lg:p-10 space-y-8" data-testid="profile-page">
        <div>
          <div className="text-xs font-semibold text-[#1E3A8A] uppercase tracking-wider">Profil</div>
          <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-zinc-900">
            Hesap Bilgilerin
          </h1>
        </div>

        <div className="rounded-2xl border border-zinc-200 bg-white p-6 lg:p-8 space-y-5">
          <div className="space-y-1.5">
            <Label>Ad Soyad</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded-xl"
              data-testid="profile-name"
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-3">
              <Label>E-posta</Label>
              {emailVerified ? (
                <span
                  data-testid="email-verified-badge"
                  className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-xs font-medium"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  E-posta doğrulandı
                </span>
              ) : (
                <span
                  data-testid="email-unverified-badge"
                  className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 text-xs font-medium"
                >
                  <MailWarning className="w-3.5 h-3.5" />
                  Doğrulanmadı
                </span>
              )}
            </div>
            <Input value={user?.email || ""} disabled className="rounded-xl bg-zinc-50" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Sınıf seviyesi</Label>
              <Select value={grade} onValueChange={setGrade}>
                <SelectTrigger className="rounded-xl" data-testid="profile-grade">
                  <SelectValue placeholder="Sınıf seç" />
                </SelectTrigger>
                <SelectContent>
                  {GRADE_LEVELS.map((g) => (
                    <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Ders</Label>
              <Select value={course} onValueChange={setCourse}>
                <SelectTrigger className="rounded-xl" data-testid="profile-course">
                  <SelectValue placeholder="Ders seç" />
                </SelectTrigger>
                <SelectContent>
                  {COURSES.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center justify-end min-h-[24px]">
            <div
              className="text-xs text-zinc-500 flex items-center gap-1.5"
              data-testid="profile-autosave-status"
              aria-live="polite"
            >
              {autosaved ? (
                <span className="inline-flex items-center gap-1 text-emerald-600">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Değişiklikler kaydedildi
                </span>
              ) : (
                "Değişiklikler otomatik kaydedilir."
              )}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-zinc-200 bg-white p-6 lg:p-8 space-y-5">
          <div>
            <h2 className="font-display text-lg font-semibold text-zinc-900">Şifre değiştir</h2>
            <p className="text-sm text-zinc-500 mt-0.5">Güvenlik için düzenli aralıklarla şifreni güncelle.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Mevcut şifre</Label>
              <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="rounded-xl" data-testid="profile-cur-pw" autoComplete="current-password" />
            </div>
            <PasswordField
              id="profile-new-pw"
              label="Yeni şifre"
              value={nw}
              onChange={setNw}
              onStrengthChange={setNwStrength}
              userInputs={[user?.name, user?.email]}
              testIdPrefix="profile-new-pw"
            />
          </div>
          <div className="flex justify-end">
            <Button
              onClick={changePw}
              disabled={pwSaving || !cur || !nwStrength.valid}
              variant="outline"
              className="rounded-full"
              data-testid="profile-change-pw"
            >
              {pwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Şifreyi Güncelle"}
            </Button>
          </div>
        </div>

        <ProfileVoicePrivacy />

        <DeleteAccountSection logout={logout} />
      </div>
    </AppShell>
  );
}

function DeleteAccountSection({ logout }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const CONFIRM = "HESABI SİL";

  const submit = async () => {
    if (confirm.trim() !== CONFIRM) {
      toast.error(`Onaylamak için '${CONFIRM}' yazmalısınız.`);
      return;
    }
    setBusy(true);
    try {
      await api.post("/account/delete", { current_password: pw, confirmation: confirm.trim() });
      toast.success("Hesabınız silindi. Görüşmek üzere.");
      try { await logout(); } catch { /* server cookie already cleared */ }
      window.location.href = "/";
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8 bg-white rounded-2xl shadow-sm border border-rose-200 p-6 space-y-4" data-testid="profile-delete-account">
      <div>
        <div className="text-sm font-semibold text-rose-800">Hesabımı Sil</div>
        <p className="mt-1 text-xs text-zinc-600">
          Kimlik ve sohbet verileriniz kalıcı olarak silinir veya anonimleştirilir; yasal olarak
          saklanması gereken fatura/ödeme kayıtları anonim referansla saklanmaya devam eder.
          Bu işlem geri alınamaz.
        </p>
      </div>
      {!open ? (
        <Button
          variant="outline"
          className="rounded-full border-rose-300 text-rose-800 hover:bg-rose-50"
          onClick={() => setOpen(true)}
          data-testid="profile-delete-open"
        >
          Hesabımı silmek istiyorum
        </Button>
      ) : (
        <div className="space-y-3">
          <div>
            <Label htmlFor="del-pw">Mevcut şifreniz</Label>
            <Input
              id="del-pw" type="password" value={pw}
              onChange={(e) => setPw(e.target.value)}
              autoComplete="current-password"
              data-testid="profile-delete-password"
            />
          </div>
          <div>
            <Label htmlFor="del-confirm">Onay için <span className="font-mono">{CONFIRM}</span> yazın</Label>
            <Input
              id="del-confirm" value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="profile-delete-confirm-input"
              placeholder={CONFIRM}
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" className="rounded-full"
              onClick={() => { setOpen(false); setPw(""); setConfirm(""); }}
              disabled={busy}
              data-testid="profile-delete-cancel"
            >
              Vazgeç
            </Button>
            <Button
              onClick={submit}
              disabled={busy || !pw || confirm.trim() !== CONFIRM}
              className="rounded-full bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="profile-delete-confirm"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Hesabımı Kalıcı Olarak Sil"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
